import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { LicenseScope, MARKETPLACE_ERROR_HANDLERS } from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT, VerificationResult } from '@platform-frontend/platform';
import {
  ClearNotifications,
  CloseWindow,
  FileService,
  InvalidStateError,
  PlannedError,
  RefreshWindow,
  ShowErrorNotification,
  ShowInfoNotification,
  convertToHttpParams,
  handleErrors,
  tapNextOrError,
  throwAndHandleError,
  throwIfNullish
} from '@platform-frontend/util';
import { EMPTY, Observable, combineLatest, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { FetchAssets } from '../asset';
import { AppFlow, FlowCanceled, FlowPreparationStarted, FlowState, SetAcademicUserType, SetFlow } from '../flow';
import {
  MSG_ACADEMIC_REGISTRATION_DEFAULT,
  MSG_ACADEMIC_REGISTRATION_FAILED_LOADING_STUDENT_ID,
  MSG_ACADEMIC_REGISTRATION_MISSING_STATE_DATA,
  MSG_ACADEMIC_SET_IP_RANGE_MISSING_STATE_DATA,
  MSG_ACADEMIC_SET_LICENSE_SCOPE_MISSING_STATE_DATA,
  MSG_ACADEMIC_VERIFICATION_DEFAULT,
  MSG_ACADEMIC_VERIFICATION_RESEND_DEFAULT,
  MSG_ACADEMIC_VERIFICATION_RESEND_MAX_MAIL_REQUESTS_EXCEEDED,
  MSG_ACADEMIC_VERIFICATION_RESEND_SERVER_UNREACHABLE,
  MSG_ACADEMIC_VERIFICATION_RESEND_SUCCESS
} from '../notification-messages';
import { ProductState } from '../product';
import {
  CancelAcademicVerification,
  CheckAcademicVerification,
  CompleteAcademicVerification,
  ResendAcademicVerification,
  SetAcademicRegistration,
  SetAcademicWebsite,
  SetIpRange,
  SetLicenseScope,
  SetStudentDetails,
  StartAcademicVerification
} from './academic.actions';
import { AcademicDetailsParameter } from './model/academic-details-parameter';
import { AcademicRegistrationData } from './model/academic-registration-data';
import { AcademicRegistrationParameters } from './model/academic-registration-parameters';
import { AcademicStateModel } from './model/academic-state-model';
import { StudentDetails } from './model/student-details';
import { StudentDetailsParameter } from './model/student-details-parameter';
import { TeacherOrOrganizationDetails } from './model/teacher-or-organization-details';
import { UserType } from './model/user-type';

const DEFAULT_STATE: AcademicStateModel = {
  academicRegistration: null,
  studentDetails: null,
  teacherOrOrganizationDetails: null,
  emailVerified: false,
  verificationRef: ''
};

@State<AcademicStateModel>({
  name: 'academic',
  defaults: DEFAULT_STATE
})
@Injectable()
export class AcademicState {
  constructor(private fileService: FileService, private http: HttpClient, private store: Store) {}

  private static readonly POLLING_TAG = Symbol('AcademicState: PollingTag');
  private static readonly RESEND_EMAIL_TAG = Symbol('AcademicState: ResendEmailTag');

  flow$: Observable<AppFlow> = this.store.select(FlowState.flow);
  productId$: Observable<string | null> = this.store.select(ProductState.productId);

  @Selector()
  static academicRegistration(state: AcademicStateModel): AcademicRegistrationData | null {
    return state.academicRegistration;
  }

  @Selector()
  static email(state: AcademicStateModel): string | null {
    return state.academicRegistration?.email ?? null;
  }

  @Selector()
  static licenseScope(state: AcademicStateModel): LicenseScope | null {
    return state.academicRegistration?.licenseScope ?? null;
  }

  @Selector([FlowState.flow])
  static userType(state: AcademicStateModel, flow: AppFlow): UserType | null {
    return flow.type === 'ACADEMIC_REGISTRATION' ? flow.startParams.start?.type ?? 'STUDENT' : null;
  }

  @Selector()
  static studentDetails(state: AcademicStateModel): StudentDetails | null {
    return state.studentDetails;
  }

  @Selector()
  static academicWebsite(state: AcademicStateModel): string | null {
    return state.teacherOrOrganizationDetails?.website ? state.teacherOrOrganizationDetails.website : null;
  }

  @Selector()
  static ipRange(state: AcademicStateModel): string[] {
    return state.teacherOrOrganizationDetails?.ipRange ? state.teacherOrOrganizationDetails.ipRange : [];
  }

  @Selector()
  static emailVerified(state: AcademicStateModel): boolean {
    return state.emailVerified;
  }

  @Action(SetFlow)
  @Action(RefreshWindow)
  @Action(FlowCanceled)
  reset(ctx: StateContext<AcademicStateModel>): void {
    ctx.setState(DEFAULT_STATE);
  }

  @Action(SetAcademicRegistration)
  setAcademicRegistration(ctx: StateContext<AcademicStateModel>, { academicRegistration }: SetAcademicRegistration): void {
    ctx.patchState({ academicRegistration });
  }

  @Action(SetLicenseScope)
  setLicenseScope(ctx: StateContext<AcademicStateModel>, { licenseScope }: SetLicenseScope): Observable<AcademicStateModel> {
    const academicRegistration = ctx.getState().academicRegistration;

    if (!academicRegistration) {
      return throwAndHandleError(new ShowErrorNotification(MSG_ACADEMIC_SET_LICENSE_SCOPE_MISSING_STATE_DATA), new PlannedError('MISSING_STATE_DATA'));
    }

    const newAcademicRegistration = { ...academicRegistration, licenseScope };
    return of(
      ctx.patchState({
        academicRegistration: newAcademicRegistration
      })
    );
  }

  @Action(SetStudentDetails)
  setStudentDetails(ctx: StateContext<AcademicStateModel>, { studentDetails }: SetStudentDetails): void {
    ctx.patchState({
      studentDetails: studentDetails
    });
  }

  @Action(SetAcademicWebsite)
  setAcademicWebsite(ctx: StateContext<AcademicStateModel>, { website }: SetAcademicWebsite): void {
    const teacherOrOrganizationDetails = ctx.getState().teacherOrOrganizationDetails;
    ctx.patchState({
      teacherOrOrganizationDetails: { ...teacherOrOrganizationDetails, website }
    });
  }

  @Action(SetIpRange)
  setIpRange(ctx: StateContext<AcademicStateModel>, { ipRange }: SetIpRange): Observable<AcademicStateModel> {
    const teacherOrOrganizationDetails = ctx.getState().teacherOrOrganizationDetails;

    if (!teacherOrOrganizationDetails) {
      return throwAndHandleError(new ShowErrorNotification(MSG_ACADEMIC_SET_IP_RANGE_MISSING_STATE_DATA), new PlannedError('MISSING_STATE_DATA'));
    }

    return of(
      ctx.patchState({
        teacherOrOrganizationDetails: { ...teacherOrOrganizationDetails, ipRange }
      })
    );
  }

  @Action(StartAcademicVerification)
  startVerification(ctx: StateContext<AcademicStateModel>): Observable<unknown> {
    const state = ctx.getState();

    return this.getUserType(state).pipe(
      switchMap(userType => this.buildStartVerificationParams(state, userType)),
      switchMap(params => this.http.post<VerificationResult>(PLATFORM_ENVIRONMENT.apiUrl + '/academic/register', params)),
      tap(({ verificationRef, verified }) =>
        ctx.patchState({
          emailVerified: verified,
          verificationRef
        })
      ),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        MISSING_STATE_DATA: new ShowErrorNotification(MSG_ACADEMIC_REGISTRATION_MISSING_STATE_DATA),
        FAILED_LOADING_STUDENT_ID: new ShowErrorNotification(MSG_ACADEMIC_REGISTRATION_FAILED_LOADING_STUDENT_ID),
        DEFAULT: new ShowErrorNotification(MSG_ACADEMIC_REGISTRATION_DEFAULT)
      })
    );
  }

  private getUserType(state: AcademicStateModel): Observable<UserType> {
    return this.flow$.pipe(
      take(1),
      map(flow => AcademicState.userType(state, flow)),
      throwIfNullish(() => new InvalidStateError('User type not available'))
    );
  }

  private buildStartVerificationParams(state: AcademicStateModel, userType: UserType): Observable<AcademicRegistrationParameters> {
    const registration = state.academicRegistration;

    if (!registration) {
      return throwError(() => new PlannedError('MISSING_STATE_DATA'));
    }

    let details$: Observable<AcademicDetailsParameter>;

    if (userType === 'STUDENT') {
      const studentDetails = state.studentDetails;
      if (studentDetails === null) {
        return throwError(() => new PlannedError('MISSING_STATE_DATA'));
      } else {
        details$ = this.buildStudentDetailsParameter(studentDetails);
      }
    } else {
      const teacherOrOrganizationDetails = state.teacherOrOrganizationDetails;
      if (teacherOrOrganizationDetails === null) {
        return throwError(() => new PlannedError('MISSING_STATE_DATA'));
      } else {
        details$ = this.buildTeacherOrOrganizationDetailsParameter(userType, registration.licenseScope, teacherOrOrganizationDetails);
      }
    }

    return combineLatest([this.getProductId(), details$]).pipe(map(([solutionId, details]) => ({ solutionId, userType, registration, details })));
  }

  private buildStudentDetailsParameter(studentDetails: StudentDetails): Observable<StudentDetailsParameter> {
    if (!studentDetails.studentId) {
      return throwError(() => new PlannedError('MISSING_STATE_DATA'));
    }
    const studentId: File = studentDetails.studentId;
    return this.fileService.encodeAsBase64(studentId).pipe(
      map(studentIdBase64 => ({
        ...studentDetails,
        studentId: studentIdBase64,
        studentIdFileName: studentId.name
      })),
      catchError(() => throwError(() => new PlannedError('FAILED_LOADING_STUDENT_ID')))
    );
  }

  private buildTeacherOrOrganizationDetailsParameter(
    userType: UserType,
    licenseScope: LicenseScope,
    teacherOrOrganizationDetails: TeacherOrOrganizationDetails
  ): Observable<TeacherOrOrganizationDetails> {
    if (userType === 'TEACHER' && licenseScope === 'PERSONAL_USE') {
      return of({ website: teacherOrOrganizationDetails.website });
    } else {
      return of(teacherOrOrganizationDetails);
    }
  }

  private getProductId(): Observable<string> {
    return this.productId$.pipe(
      take(1),
      throwIfNullish(() => new InvalidStateError('Product id not available'))
    );
  }

  @Action(CheckAcademicVerification)
  checkVerification(ctx: StateContext<AcademicStateModel>, { timeoutInMilliseconds }: CheckAcademicVerification): Observable<unknown> {
    const params = convertToHttpParams({ verificationRef: this.getVerificationRef(ctx) });
    const nOptions = { keepExisting: true, tags: [AcademicState.POLLING_TAG] };
    return this.http.get<VerificationResult>(PLATFORM_ENVIRONMENT.apiUrl + '/academic/verified', { params }).pipe(
      timeout({ each: timeoutInMilliseconds - 50, with: () => throwError(() => new PlannedError('SERVER_UNREACHABLE')) }),
      tap(({ verified }) => ctx.patchState({ emailVerified: verified })),
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(AcademicState.POLLING_TAG))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        SERVER_UNREACHABLE: new ShowErrorNotification(MSG_ACADEMIC_VERIFICATION_DEFAULT, nOptions),
        NOT_FOUND: new CloseWindow(),
        DEFAULT: new ShowErrorNotification(MSG_ACADEMIC_VERIFICATION_DEFAULT, nOptions)
      })
    );
  }

  @Action(ResendAcademicVerification)
  resendVerification(ctx: StateContext<AcademicStateModel>): Observable<unknown> {
    const verificationRef = this.getVerificationRef(ctx);
    const nOptions = { keepExisting: true, tags: [AcademicState.RESEND_EMAIL_TAG] };
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/academic/resendConfirmationMail', { verificationRef }).pipe(
      tapNextOrError(() => ctx.dispatch(new ClearNotifications(AcademicState.RESEND_EMAIL_TAG))),
      switchMap(() => ctx.dispatch(new ShowInfoNotification(MSG_ACADEMIC_VERIFICATION_RESEND_SUCCESS, nOptions))),
      handleErrors(MARKETPLACE_ERROR_HANDLERS, {
        MAX_MAIL_REQUESTS_EXCEEDED: new ShowErrorNotification(MSG_ACADEMIC_VERIFICATION_RESEND_MAX_MAIL_REQUESTS_EXCEEDED, nOptions),
        SERVER_UNREACHABLE: new ShowErrorNotification(MSG_ACADEMIC_VERIFICATION_RESEND_SERVER_UNREACHABLE, nOptions),
        DEFAULT: new ShowErrorNotification(MSG_ACADEMIC_VERIFICATION_RESEND_DEFAULT, nOptions)
      })
    );
  }

  @Action(CompleteAcademicVerification)
  completeVerification(ctx: StateContext<AcademicStateModel>): Observable<unknown> {
    const verificationRef = this.getVerificationRef(ctx);
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/academic/complete', { verificationRef }).pipe(
      switchMap(() => ctx.dispatch(new FetchAssets())),
      tap(() => ctx.patchState({ emailVerified: false, verificationRef: '' }))
    );
  }

  @Action(CancelAcademicVerification)
  cancelVerification(ctx: StateContext<AcademicStateModel>): Observable<unknown> {
    const verificationRef = this.getVerificationRef(ctx);
    return this.http.post<void>(PLATFORM_ENVIRONMENT.apiUrl + '/academic/cancel', { verificationRef }).pipe(
      catchError(() => of(null)),
      tap(() => ctx.patchState({ emailVerified: false, verificationRef: '' }))
    );
  }

  private getVerificationRef(ctx: StateContext<AcademicStateModel>): string {
    const { verificationRef } = ctx.getState();

    if (verificationRef === '') {
      throw new InvalidStateError('Academic verification is not initialized.');
    }

    return verificationRef;
  }

  @Action(FlowPreparationStarted)
  fetchAcademicData(ctx: StateContext<AcademicStateModel>, { flow }: FlowPreparationStarted): Observable<unknown> {
    if (flow.type !== 'ACADEMIC_REGISTRATION') {
      return EMPTY;
    }
    return this.http.get<AcademicRegistrationParameters | null>(PLATFORM_ENVIRONMENT.apiUrl + '/account/academic').pipe(
      switchMap(response => (response !== null ? this.applyState(ctx, response) : EMPTY)),
      catchError(() => EMPTY)
    );
  }

  private applyState(ctx: StateContext<AcademicStateModel>, params: AcademicRegistrationParameters): Observable<unknown> {
    const state: AcademicStateModel = {
      ...DEFAULT_STATE,
      academicRegistration: params.registration
    };
    if (params.userType === 'STUDENT') {
      const details = <StudentDetailsParameter>params.details;
      state.studentDetails = { studentNumber: details.studentNumber, studentId: null };
    } else {
      const details = params.details as TeacherOrOrganizationDetails;
      state.teacherOrOrganizationDetails = { ...details };
    }

    ctx.patchState(state);
    return ctx.dispatch(new SetAcademicUserType(params.userType));
  }
}
